import * as React from 'react';
import { Badge } from 'app/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from 'app/components/ui/avatar';
import { Switch } from 'app/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from 'app/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'app/components/ui/alert-dialog';
import { useToast } from 'app/components/ui/use-toast';
import { usePaymentMethodsSlice } from './slice';
import { Pencil, Eye, EyeOff, MoreHorizontal } from 'lucide-react';
import { Button } from 'app/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { selectSettings } from 'app/slice/selectors';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { mediaBaseUrl } from 'utils/api/endpoints';
import {
  getPaymentGatewayFields,
  PaymentGatewayField,
} from './paymentGatewayConfig';
import { useMediaSlice } from '../Media/slice';
import { PAYMENT_METHOD } from 'utils/constants';

// Add this type at the top of the file (or import from your types if available)
type PaymentGateway = {
  gateway_id: number;
  name: string;
  description: string;
  type: string;
  payment_method: number;
  status: number;
  secret_key: string | null;
  public_key: string | null;
  position: number;
  is_default: number;
  extra_config: string | null;
  created_at: string;
  updated_at: string;
  gateway_type: string;
  icon: string | null;
};

const statusMap = {
  0: { label: 'Inactive', color: 'bg-red-100 text-red-800' },
  1: { label: 'Active', color: 'bg-green-100 text-green-800' },
};

const typeMap = {
  online: 'Online',
  offline: 'Offline',
  wallet: 'Wallet',
};

export default function PaymentMethods() {
  const navigate = useNavigate();
  const [modal, setModal] = React.useState<{
    open: boolean;
    gateway: PaymentGateway | null;
  }>({ open: false, gateway: null });
  const [keyInputs, setKeyInputs] = React.useState<Record<string, string>>({});
  const [isUploadingIcon, setIsUploadingIcon] = React.useState(false);
  const [defaultConfirmModal, setDefaultConfirmModal] = React.useState<{
    open: boolean;
    gateway: PaymentGateway | null;
  }>({ open: false, gateway: null });
  const [showSecretKeys, setShowSecretKeys] = React.useState<{
    [key: number]: boolean;
  }>({});
  const { toast } = useToast();
  const {
    useImmediateGetPaymentMethodsQuery,
    useUpdatePaymentMethodMutation,
    useSetDefaultPaymentMethodMutation,
    useChangePaymentMethodOrderMutation,
  } = usePaymentMethodsSlice();
  const [
    updatePaymentMethod,
    { isLoading: isUpdating, isSuccess: isUpdated, isError: isErrorUpdating },
  ] = useUpdatePaymentMethodMutation();
  const [
    setDefaultPaymentMethod,
    {
      isLoading: isSettingDefault,
      isSuccess: isDefaultSet,
      isError: isErrorSettingDefault,
    },
  ] = useSetDefaultPaymentMethodMutation();
  const [
    changePaymentMethodOrder,
    {
      isLoading: isChangingOrder,
      isSuccess: isChangedOrder,
      isError: isErrorChangingOrder,
    },
  ] = useChangePaymentMethodOrderMutation();
  const { useUploadMediaMutation } = useMediaSlice();
  const [
    uploadFile,
    {
      isLoading: isUploadingImage,
      error: fileUploadingError,
      data: fileUploaded,
    },
  ] = useUploadMediaMutation();

  const settings = useSelector(selectSettings);

  // Fetch active and inactive gateways from API
  const {
    data: activeGatewaysRaw = [],
    isLoading: loadingActive,
    refetch: refetchActive,
  } = useImmediateGetPaymentMethodsQuery({ status: 1 });
  const {
    data: inactiveGatewaysRaw = [],
    isLoading: loadingInactive,
    refetch: refetchInactive,
  } = useImmediateGetPaymentMethodsQuery({ status: 0 });

  // Normalize for possible {gateways: []} API response
  const activeGateways: PaymentGateway[] = Array.isArray(activeGatewaysRaw)
    ? activeGatewaysRaw
    : Array.isArray(activeGatewaysRaw?.gateways)
      ? activeGatewaysRaw.gateways
      : [];
  const inactiveGateways: PaymentGateway[] = Array.isArray(inactiveGatewaysRaw)
    ? inactiveGatewaysRaw
    : Array.isArray(inactiveGatewaysRaw?.gateways)
      ? inactiveGatewaysRaw.gateways
      : [];

  React.useEffect(() => {
    // getPaymentMethods(); // This line is no longer needed as data is fetched directly
  }, []);

  // Stub: Replace with your real mutation
  const handleUpdatePaymentMethod = async (gatewayId, update) => {
    updatePaymentMethod({ gateway_id: gatewayId, ...update });
  };

  // Handle setting default gateway
  const handleSetDefault = async gatewayId => {
    setDefaultPaymentMethod({ gateway_id: gatewayId });
  };

  // Handle default confirmation
  const handleDefaultConfirmation = gateway => {
    setDefaultConfirmModal({ open: true, gateway });
  };

  const confirmSetDefault = () => {
    if (defaultConfirmModal.gateway) {
      handleSetDefault(defaultConfirmModal.gateway.gateway_id);
      setDefaultConfirmModal({ open: false, gateway: null });
    }
  };

  // Handle switch toggle
  const handleStatusChange = gateway => {
    if (gateway.status === 1) {
      // Deactivate
      handleUpdatePaymentMethod(gateway.gateway_id, { status: 0 });
      toast({ title: `${gateway.name} deactivated`, variant: 'default' });
    } else {
      // If missing required fields, open modal
      const gatewayFields = getPaymentGatewayFields(gateway.gateway_type);
      const hasRequiredFields = gatewayFields.every(field => {
        if (!field.required) return true;
        const fieldValue = gateway[field.key as keyof PaymentGateway];
        return fieldValue && fieldValue.toString().trim() !== '';
      });

      if (!hasRequiredFields) {
        setModal({ open: true, gateway });
        // Initialize inputs with current gateway values
        const initialInputs: Record<string, string> = {
          name: gateway.name || '',
          description: gateway.description || '',
          icon: gateway.icon || '',
        };
        gatewayFields.forEach(field => {
          if (field.key === 'email' && gateway.gateway_type === 'paypal') {
            // Extract email from PayPal's extra_config JSON
            try {
              const extraConfig = gateway.extra_config
                ? JSON.parse(gateway.extra_config)
                : {};
              initialInputs[field.key] = extraConfig.business_email || '';
            } catch {
              initialInputs[field.key] = '';
            }
          } else {
            initialInputs[field.key] =
              gateway[field.key as keyof PaymentGateway]?.toString() || '';
          }
        });
        setKeyInputs(initialInputs);
      } else {
        handleUpdatePaymentMethod(gateway.gateway_id, { status: 1 });
        toast({ title: `${gateway.name} activated`, variant: 'success' });
        // Data will be automatically updated by RTK Query cache invalidation
      }
    }
  };

  // Toggle secret key visibility
  const toggleSecretKeyVisibility = (gatewayId: number) => {
    setShowSecretKeys(prev => ({
      ...prev,
      [gatewayId]: !prev[gatewayId],
    }));
  };

  // Handle position change via dropdown
  const handlePositionChange = async (
    gatewayId: number,
    newPosition: number,
  ) => {
    try {
      await changePaymentMethodOrder({
        gateway_id: gatewayId,
        new_position: newPosition,
      }).unwrap();

      toast({
        title: 'Order Updated',
        description: `Position changed to ${newPosition}`,
        variant: 'default',
      });
    } catch (error) {
      console.error('Position change error:', error);
      toast({
        title: 'Error',
        description: 'Failed to update payment method order',
        variant: 'destructive',
      });
    }
  };

  // Handle modal submit
  const handleModalSubmit = e => {
    e.preventDefault();
    if (!modal.gateway) return;

    // Validate required fields
    if (!keyInputs.name || keyInputs.name.trim() === '') {
      toast({
        title: 'Missing Required Fields',
        description: 'Please fill in: Gateway Name',
        variant: 'destructive',
      });
      return;
    }

    const gatewayFields = getPaymentGatewayFields(modal.gateway.gateway_type);
    const missingRequiredFields = gatewayFields.filter(
      field =>
        field.required &&
        (!keyInputs[field.key] || keyInputs[field.key].trim() === ''),
    );

    if (missingRequiredFields.length > 0) {
      toast({
        title: 'Missing Required Fields',
        description: `Please fill in: ${missingRequiredFields
          .map(f => f.label)
          .join(', ')}`,
        variant: 'destructive',
      });
      return;
    }

    // Prepare update data with dynamic fields
    const updateData: Record<string, any> = {
      status: 1,
      name: keyInputs.name,
      description: keyInputs.description,
      icon: keyInputs.icon,
    };

    gatewayFields.forEach(field => {
      if (keyInputs[field.key]) {
        // Map gateway-specific fields to API payload structure
        if (field.key === 'email') {
          // Email goes to extra_config as JSON object for PayPal
          if (modal.gateway.gateway_type === 'paypal') {
            updateData.extra_config = JSON.stringify({
              business_email: keyInputs[field.key],
            });
          } else {
            updateData.extra_config = keyInputs[field.key];
          }
        } else if (field.key === 'public_key') {
          // Public key (like Razorpay Key ID) goes to public_key
          updateData.public_key = keyInputs[field.key];
        } else if (field.key === 'secret_key') {
          // Secret key goes to secret_key
          updateData.secret_key = keyInputs[field.key];
        } else {
          // Other fields keep their original key
          updateData[field.key] = keyInputs[field.key];
        }
      }
    });

    handleUpdatePaymentMethod(modal.gateway.gateway_id, updateData);
  };

  React.useEffect(() => {
    if (isUpdated) {
      setModal({ open: false, gateway: null });
      toast({
        title: `${modal?.gateway?.name || 'Gateway'} activated and keys saved`,
        variant: 'success',
      });
      // Refetch data to show updated status
      refetchActive();
      refetchInactive();
    }
    if (isErrorUpdating) {
      toast({
        title: 'Error updating payment method',
        variant: 'destructive',
      });
    }
    if (isDefaultSet) {
      toast({
        title: 'Default payment method updated successfully',
        variant: 'success',
      });
      // Refetch data to show updated default status
      refetchActive();
      refetchInactive();
    }

    if (isErrorSettingDefault) {
      toast({
        title: 'Error setting default payment method',
        variant: 'destructive',
      });
    }

    if (isChangedOrder) {
      // Refetch the data to show updated order
      refetchActive();
      refetchInactive();
    }

    if (isErrorChangingOrder) {
      toast({
        title: 'Error changing payment method order',
        variant: 'destructive',
      });
    }
  }, [
    isUpdated,
    isErrorUpdating,
    isDefaultSet,
    isErrorSettingDefault,
    isChangedOrder,
    isErrorChangingOrder,
  ]);

  // Filter active/inactive - REMOVED

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Payment Methods</h1>

      {/* Active Gateways Table */}
      <div className="mb-8">
        <div className="bg-white rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px] px-4 py-3">Logo</TableHead>
                <TableHead className="min-w-[220px] px-4 py-3">Name</TableHead>
                <TableHead className="w-[120px] px-4 py-3">Status</TableHead>
                <TableHead className="min-w-[220px] px-4 py-3">
                  Configuration Fields
                </TableHead>
                <TableHead className="w-[100px] px-4 py-3">Position</TableHead>
                <TableHead className="w-[140px] px-4 py-3">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeGateways.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-gray-500 py-8"
                  >
                    No active payment gateways found.
                  </TableCell>
                </TableRow>
              ) : (
                activeGateways.map(gateway => renderGatewayRow(gateway, true))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Inactive Gateways Table */}
      <div>
        <h2 className="text-xl font-semibold mb-4 text-gray-600">
          Available Payment Methods
        </h2>
        <div className="bg-white rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px] px-4 py-3">Logo</TableHead>
                <TableHead className="min-w-[220px] px-4 py-3">Name</TableHead>
                <TableHead className="w-[120px] px-4 py-3">Status</TableHead>
                <TableHead className="min-w-[220px] px-4 py-3">
                  Configuration Fields
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inactiveGateways.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center text-gray-500 py-8"
                  >
                    No inactive payment gateways found.
                  </TableCell>
                </TableRow>
              ) : (
                inactiveGateways.map(gateway =>
                  renderGatewayRow(gateway, false),
                )
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      {/* Modal for entering keys */}
      <Dialog
        open={modal.open}
        onOpenChange={open => setModal(m => ({ ...m, open }))}
      >
        <DialogContent className="h-auto max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleModalSubmit} className="">
            <DialogHeader>
              <DialogTitle>
                {modal.gateway && modal.gateway.secret_key
                  ? `Edit ${modal.gateway.name} Configuration`
                  : `Configure ${modal.gateway?.name || 'Gateway'}`}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 p-5">
              {/* Gateway Name Field */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Gateway Name <span className="text-red-500 ml-1">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  The display name for this payment gateway
                </p>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={keyInputs.name || ''}
                  onChange={e =>
                    setKeyInputs(k => ({
                      ...k,
                      name: e.target.value,
                    }))
                  }
                  required
                  placeholder="Enter gateway display name"
                />
              </div>

              {/* Gateway Description Field */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Gateway Description
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  A brief description of this payment gateway
                </p>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm resize-none"
                  rows={3}
                  value={keyInputs.description || ''}
                  onChange={e =>
                    setKeyInputs(k => ({
                      ...k,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Enter a description for this payment gateway"
                />
              </div>

              {/* Gateway Icon Field */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Gateway Icon
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Upload a custom icon for this payment gateway (PNG, JPG, SVG)
                </p>
                <div className="space-y-3">
                  {/* Current Icon Preview */}
                  {keyInputs.icon && (
                    <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                      <div className="flex-shrink-0">
                        <img
                          src={`${mediaBaseUrl}/payment-gateway/${keyInputs.icon}`}
                          alt="Current icon"
                          className="w-12 h-12 object-contain rounded"
                          onError={e => {
                            // Fallback to avatar if image fails to load
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling.style.display =
                              'block';
                          }}
                        />
                        <Avatar className="w-12 h-12 hidden">
                          <AvatarFallback>
                            {modal.gateway?.name?.[0] || 'G'}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          {isUploadingIcon ? 'Uploading...' : 'Current Icon'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {keyInputs.icon}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* File Upload Input */}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                    className="w-full border rounded px-3 py-2 text-sm"
                    disabled={isUploadingIcon}
                    onChange={async e => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      setIsUploadingIcon(true);

                      const formData = new FormData();
                      formData.append('payment-gateway', file);

                      try {
                        const response = await uploadFile(formData).unwrap();

                        if (response?.response?.['payment-gateway']?.[0]) {
                          const uploadedFileName =
                            response.response['payment-gateway'][0];
                          setKeyInputs(k => ({
                            ...k,
                            icon: uploadedFileName,
                          }));

                          toast({
                            title: 'Icon uploaded successfully',
                            description: 'Gateway icon has been uploaded',
                            variant: 'success',
                          });
                        } else {
                          throw new Error('Invalid response format');
                        }
                      } catch (error) {
                        toast({
                          title: 'Failed to upload icon',
                          description: 'Please try again',
                          variant: 'destructive',
                        });
                      } finally {
                        setIsUploadingIcon(false);
                      }
                    }}
                  />

                  {isUploadingIcon && (
                    <p className="text-xs text-blue-600">Uploading icon...</p>
                  )}
                </div>
              </div>

              {/* Gateway-specific fields */}
              {modal.gateway &&
                (() => {
                  const gatewayFields = getPaymentGatewayFields(
                    modal.gateway.gateway_type,
                  );
                  return gatewayFields.map((field: PaymentGatewayField) => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium mb-1">
                        {field.label}
                        {field.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </label>
                      {field.description && (
                        <p className="text-xs text-gray-500 mb-2">
                          {field.description}
                        </p>
                      )}
                      <input
                        type={
                          field.type === 'password'
                            ? 'password'
                            : field.type === 'email'
                              ? 'email'
                              : 'text'
                        }
                        className="w-full border rounded px-3 py-2 text-sm"
                        value={keyInputs[field.key] || ''}
                        onChange={e =>
                          setKeyInputs(k => ({
                            ...k,
                            [field.key]: e.target.value,
                          }))
                        }
                        required={field.required}
                        placeholder={field.placeholder}
                      />
                    </div>
                  ));
                })()}
            </div>
            <div className="p-5">
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button">Cancel</Button>
                </DialogClose>
                <Button
                  type="submit"
                  disabled={
                    isUpdating ||
                    isUploadingIcon ||
                    !keyInputs.name ||
                    keyInputs.name.trim() === '' ||
                    (modal.gateway &&
                      (() => {
                        const gatewayFields = getPaymentGatewayFields(
                          modal.gateway.gateway_type,
                        );
                        return gatewayFields.some(
                          field =>
                            field.required &&
                            (!keyInputs[field.key] ||
                              keyInputs[field.key].trim() === ''),
                        );
                      })())
                  }
                >
                  {isUpdating
                    ? 'Saving...'
                    : modal.gateway && modal.gateway.secret_key
                      ? 'Update Configuration'
                      : 'Save & Activate'}
                </Button>
              </DialogFooter>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Confirmation Dialog for Setting Default */}
      <AlertDialog
        open={defaultConfirmModal.open}
        onOpenChange={open => setDefaultConfirmModal(m => ({ ...m, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Set as Default Payment Method</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to set "{defaultConfirmModal.gateway?.name}"
              as the default payment method? This will replace the current
              default payment method.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSetDefault}
              disabled={isSettingDefault}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSettingDefault ? 'Setting...' : 'Set as Default'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  // Helper to render a gateway table row
  function renderGatewayRow(gateway: PaymentGateway, isActive: boolean) {
    const logoSrc = `/images/${gateway.gateway_type}.png`;
    const isSecretKeyVisible = showSecretKeys[gateway.gateway_id] || false;

    return (
      <TableRow
        key={gateway.gateway_id}
        className={`${isActive && gateway.is_default ? 'bg-blue-50' : ''}`}
      >
        {/* Logo Column */}
        <TableCell className="px-4 py-4">
          <div className="flex justify-center">
            {gateway.icon ? (
              <img
                src={`${mediaBaseUrl}/payment-gateway/${gateway.icon}`}
                alt={gateway.name + ' logo'}
                className="object-contain w-12 h-12"
              />
            ) : (
              <Avatar className="w-12 h-12">
                <AvatarFallback>{gateway.name[0]}</AvatarFallback>
              </Avatar>
            )}
          </div>
        </TableCell>

        {/* Name Column */}
        <TableCell className="px-4 py-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">
                {gateway.name.replace(/\s+\d+$/, '')}
              </span>
              {isActive && gateway.is_default ? (
                <Badge className="bg-primary text-white text-xs px-2 py-1">
                  Default
                </Badge>
              ) : (
                ''
              )}
            </div>
            <div className="text-sm text-gray-600">
              Payment Provider:{' '}
              <span className="text-blue-800 bg-blue-100 px-2 py-1 rounded-md text-xs">
                {' '}
                {PAYMENT_METHOD[gateway.payment_method] || 'Unknown Gateway'}
              </span>
            </div>
            {/* <div className="text-sm text-gray-500 capitalize">
              {(typeMap[gateway.type] || gateway.type).replace(/\s+\d+$/, '')}
            </div> */}
            {gateway.description && (
              <div className="text-xs text-gray-500 leading-relaxed">
                {gateway.description.replace(/\s+\d+$/, '')}
              </div>
            )}
          </div>
        </TableCell>

        {/* Status Column */}
        <TableCell className="px-4 py-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={gateway.status === 1}
              onCheckedChange={() => handleStatusChange(gateway)}
              className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-gray-300"
            />
            <Badge
              className={`text-xs px-2 py-1 ${statusMap[gateway.status].color}`}
            >
              {statusMap[gateway.status].label}
            </Badge>
          </div>
        </TableCell>

        {/* Configuration Fields Column - Only for active gateways */}
        {isActive && (
          <TableCell className="px-4 py-4">
            <div className="space-y-2">
              {(() => {
                const gatewayFields = getPaymentGatewayFields(
                  gateway.gateway_type,
                );
                return gatewayFields.map((field: PaymentGatewayField) => (
                  <div key={field.key} className="flex items-center gap-2">
                    <div className="text-xs text-gray-600 min-w-[80px]">
                      {field.label}:
                    </div>
                    <div className="flex-1">
                      {(() => {
                        let fieldValue =
                          gateway[field.key as keyof PaymentGateway];

                        // Handle PayPal email from extra_config
                        if (
                          field.key === 'email' &&
                          gateway.gateway_type === 'paypal'
                        ) {
                          try {
                            const extraConfig = gateway.extra_config
                              ? JSON.parse(gateway.extra_config)
                              : {};
                            fieldValue = extraConfig.business_email || null;
                          } catch {
                            fieldValue = null;
                          }
                        }

                        return fieldValue ? (
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded border flex-1 truncate">
                              {field.type === 'password'
                                ? isSecretKeyVisible
                                  ? fieldValue
                                  : '••••••••••••••••'
                                : fieldValue}
                            </code>
                            {field.type === 'password' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  toggleSecretKeyVisibility(gateway.gateway_id)
                                }
                                className="h-6 w-6 p-0 hover:bg-gray-100"
                              >
                                {isSecretKeyVisible ? (
                                  <EyeOff className="w-3 h-3" />
                                ) : (
                                  <Eye className="w-3 h-3" />
                                )}
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">
                            Not configured
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </TableCell>
        )}

        {/* Position Column - Only for active gateways */}
        {isActive && (
          <TableCell className="px-4 py-4">
            <div className="flex justify-center">
              <Select
                value={gateway.position?.toString() || '1'}
                onValueChange={value =>
                  handlePositionChange(gateway.gateway_id, parseInt(value))
                }
                disabled={isChangingOrder}
              >
                <SelectTrigger className="w-16 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(
                    { length: activeGateways.length },
                    (_, i) => i + 1,
                  ).map(position => (
                    <SelectItem key={position} value={position.toString()}>
                      {position}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </TableCell>
        )}

        {/* Actions Column - Only for active gateways */}
        {isActive && (
          <TableCell className="px-4 py-4">
            <div className="flex items-center justify-start">
              {/* Three dots menu for all actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 hover:bg-gray-100"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {/* Manage option for manual payment gateways */}
                  {gateway.payment_method === 2 && (
                    <DropdownMenuItem
                      onClick={() => {
                        navigate('/offline-payment-methods');
                      }}
                      className="cursor-pointer"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Manage
                    </DropdownMenuItem>
                  )}
                  {/* Edit option only for non-wallet and non-offline gateways */}
                  {gateway.type !== 'wallet' &&
                    gateway.type !== 'offline' &&
                    gateway.payment_method !== 2 && (
                      <DropdownMenuItem
                        onClick={() => {
                          setModal({ open: true, gateway });
                          // Initialize inputs with current gateway values
                          const gatewayFields = getPaymentGatewayFields(
                            gateway.gateway_type,
                          );
                          const initialInputs: Record<string, string> = {
                            name: gateway.name || '',
                            description: gateway.description || '',
                            icon: gateway.icon || '',
                          };
                          gatewayFields.forEach(field => {
                            if (
                              field.key === 'email' &&
                              gateway.gateway_type === 'paypal'
                            ) {
                              // Extract email from PayPal's extra_config JSON
                              try {
                                const extraConfig = gateway.extra_config
                                  ? JSON.parse(gateway.extra_config)
                                  : {};
                                initialInputs[field.key] =
                                  extraConfig.business_email || '';
                              } catch {
                                initialInputs[field.key] = '';
                              }
                            } else {
                              initialInputs[field.key] =
                                gateway[
                                  field.key as keyof PaymentGateway
                                ]?.toString() || '';
                            }
                          });
                          setKeyInputs(initialInputs);
                        }}
                        className="cursor-pointer"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit Configuration
                      </DropdownMenuItem>
                    )}
                  {/* Set as Default option for active gateways that are not already default */}
                  {isActive && !gateway.is_default && (
                    <DropdownMenuItem
                      onClick={() => handleDefaultConfirmation(gateway)}
                      disabled={isSettingDefault}
                      className="cursor-pointer"
                    >
                      {isSettingDefault ? 'Setting...' : 'Set as Default'}
                    </DropdownMenuItem>
                  )}
                  {/* Show message if already default */}
                  {isActive && gateway.is_default ? (
                    <DropdownMenuItem disabled className="text-gray-500">
                      Already Default
                    </DropdownMenuItem>
                  ) : (
                    ''
                  )}
                  {/* Add more actions here if needed in the future */}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </TableCell>
        )}

        {/* Configuration Fields Column - Only for inactive gateways */}
        {!isActive && (
          <TableCell className="px-4 py-4">
            <div className="space-y-2">
              {(() => {
                const gatewayFields = getPaymentGatewayFields(
                  gateway.gateway_type,
                );
                return gatewayFields.map((field: PaymentGatewayField) => (
                  <div key={field.key} className="flex items-center gap-2">
                    <div className="text-xs text-gray-600 min-w-[80px]">
                      {field.label}:
                    </div>
                    <div className="flex-1">
                      {(() => {
                        let fieldValue =
                          gateway[field.key as keyof PaymentGateway];

                        // Handle PayPal email from extra_config
                        if (
                          field.key === 'email' &&
                          gateway.gateway_type === 'paypal'
                        ) {
                          try {
                            const extraConfig = gateway.extra_config
                              ? JSON.parse(gateway.extra_config)
                              : {};
                            fieldValue = extraConfig.business_email || null;
                          } catch {
                            fieldValue = null;
                          }
                        }

                        return fieldValue ? (
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded border flex-1 truncate">
                              {field.type === 'password'
                                ? isSecretKeyVisible
                                  ? fieldValue
                                  : '••••••••••••••••'
                                : fieldValue}
                            </code>
                            {field.type === 'password' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  toggleSecretKeyVisibility(gateway.gateway_id)
                                }
                                className="h-6 w-6 p-0 hover:bg-gray-100"
                              >
                                {isSecretKeyVisible ? (
                                  <EyeOff className="w-3 h-3" />
                                ) : (
                                  <Eye className="w-3 h-3" />
                                )}
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">
                            Not configured
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </TableCell>
        )}
      </TableRow>
    );
  }
}
